import { AutoBrandFont, querySite, updateBrand } from 'libs/e1api/autoBrand';
import { BrandColorList, BrandState } from 'modules/User';
import { toHex } from 'utils/color';

export type AutoBrandEntity = {
  colors: BrandColorList;
  logos: Blob[];
  fonts: AutoBrandFont[];
  suggestedFonts: string;
};

/**
 * Helper to parse and normalize data from brand
 * @param brandURL string
 * @returns AutoBrandEntity
 */
export const getAutoBrand = async (brandURL: string, useFontFallback?: boolean): Promise<AutoBrandEntity> => {
  try {
    const { logos, colors, fonts, suggestedFonts = '' } = await querySite(brandURL, useFontFallback);
    let logoBlobs: Blob[] = [];
    let palettes: BrandColorList = [];

    if (colors.length === 0 && logos.length === 0) {
      return { logos: [], colors: [], fonts: [], suggestedFonts: '' };
    }

    try {
      logoBlobs = await Promise.all(logos.map((logo) => fetch(logo).then((res) => res.blob())));
    } catch (err) {
      console.error(err);
    }

    palettes = colors.map((color) => ({
      color: toHex(`rgb(${color[0]}, ${color[1]}, ${color[2]})`),
      label: '',
    }));

    return { logos: logoBlobs, colors: palettes, fonts, suggestedFonts };
  } catch (error) {
    console.error('getAutoBrand', error);
    return { logos: [], colors: [], fonts: [], suggestedFonts: '' };
  }
};

/**
 * Helper to check if the auto brand fonts response has fonts (that we support)
 *
 * @param brandFonts AutoBrandFont[]
 * @returns boolean
 */
export const hasBrandFonts = (brandFonts?: AutoBrandFont[]) => {
  if (!brandFonts) return false;
  if (!Array.isArray(brandFonts)) return false;
  if (!brandFonts.length) return false;

  // check if there is at least one font that is venngage font
  return brandFonts.some((f) => f?.isVenngageFont);
};

/**
 * Helper to create a mixpanel event object for auto brand fonts
 *
 * @param fonts AutoBrandFont[]
 * @returns object
 */
export const createMixpanelAutoFontEvent = (fonts: AutoBrandFont[]) => {
  const mixpanelFont: {
    'fonts found': number;
    'brand fonts': string[] | string;
    'unavailable google fonts': string[] | string;
  } = { 'fonts found': 0, 'brand fonts': [], 'unavailable google fonts': [] };

  fonts.forEach((font) => {
    if (font && font.fontFamily) {
      if ((font.type === 'title' || font.type === 'text') && font.isVenngageFont) {
        mixpanelFont['fonts found']++;
        (mixpanelFont['brand fonts'] as string[]).push(font.fontFamily);
      }
      if (font.origin === 'google' && !font.isVenngageFont) {
        (mixpanelFont['unavailable google fonts'] as string[]).push(font.fontFamily);
      }
    }
  });

  // Convert arrays to comma-separated strings
  mixpanelFont['brand fonts'] = (mixpanelFont['brand fonts'] as string[]).join(', ');
  mixpanelFont['unavailable google fonts'] = (mixpanelFont['unavailable google fonts'] as string[]).join(', ');

  return mixpanelFont;
};

export const updateBrandFonts = async (brand: BrandState, fontName: string) => {
  try {
    const fontSize: Record<string, number> = {
      title: 50,
      subtitle: 30,
      text: 20,
    };
    const payload: Record<string, any> = {};
    for (const type of ['title', 'subtitle', 'text']) {
      payload[type] = {
        fontFamily: fontName,
        bold: false,
        underline: false,
        italic: false,
        fontSize: fontSize[type],
      };
    }

    const response = await updateBrand({
      name: brand.name,
      id: brand.id,
      ...payload,
    });

    if (response?.id) {
      return {
        success: true,
      };
    }

    return {
      success: false,
    };
  } catch (error) {
    console.log('updateBrandFonts', error);

    return {
      success: false,
    };
  }
};
